import React, { useState, useRef, useEffect } from 'react';
import { Trash2, ChevronUp, ChevronDown, AlignLeft, AlignCenter, AlignRight, Upload, X, Loader2, GitBranch } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Checkbox } from '../../ui/Checkbox';
import { Select } from '../../ui/Select';
import { FieldLabelInput } from '../fieldProps/FieldLabelInput';
import { FieldPlaceholderInput } from '../fieldProps/FieldPlaceholderInput';
import { FieldHelpTextInput } from '../fieldProps/FieldHelpTextInput';
import { FieldRequiredToggle } from '../fieldProps/FieldRequiredToggle';
import { FieldOptionsEditor } from '../fieldProps/FieldOptionsEditor';
import type { FormField, FormPage, FieldAlignment, FieldOptionScore, FieldRule, ScoreVariable, FormSettings, LogoPosition } from '../../../types/forms';
import { LAYOUT_OPTION_TYPES, NON_INPUT_FIELD_TYPES, PAGE_EXCLUDED_FIELD_TYPES } from '../../../types/forms';
import { getEffectiveAlignment } from '../../../lib/getAlignmentClass';
import { AlignmentScopePrompt } from './AlignmentScopePrompt';
import { RuleEditorModal } from './RuleEditorModal';
import { supabase, getPublicStorageUrl } from '../../../lib/supabase';
import { useUpgradeGate } from '../../billing/UpgradeGate';

// Types with scoring
const SCORABLE_TYPES = [
  'radio', 'select', 'checkbox', 'multiselect', 'picture_choice',
  'yes_no', 'opinion_scale', 'nps', 'rating',
];

const LOGO_MAX_SIZE = 500 * 1024; // 500KB
const LOGO_ACCEPTED = '.png,.jpg,.jpeg,.webp,.svg';
const BG_MAX_SIZE = 5 * 1024 * 1024; // 5MB (Story saas-1.6)
const BG_ACCEPTED = '.png,.jpg,.jpeg,.webp';

interface SlidePropertiesPanelProps {
  field: FormField | null;
  allFields: FormField[];
  onUpdate: (fieldId: string, updates: Partial<FormField>) => void;
  onUpdateConfig: (fieldId: string, config: Record<string, any>) => void;
  onClose: () => void;
  scoringConfig?: { enabled: boolean; variables?: { id: string; name: string; ranges: any[] }[] };
  onUpdateOptionScore?: (fieldId: string, optIdx: number, varId: string, pts: number) => void;
  onUpdateRules?: (fieldId: string, rules: FieldRule[]) => void;
  // Logo / Marca
  formSettings?: FormSettings;
  onUpdateSettings?: (updates: Partial<FormSettings>) => void;
  userId?: string;
  formId?: string;
  // Page properties (AC9 - Story 5.2)
  selectedPage?: FormPage | null;
  onUpdatePage?: (pageId: string, updates: Partial<FormPage>) => void;
  // All pages for field width context (Story 5.3)
  pages?: FormPage[];
  // Bulk alignment (mobile-ui.6.F F4) — quando presente, mudar alinhamento
  // pergunta "Aplicar a todas as perguntas?" (Só esta / Todas).
  onSetAlignmentAll?: (alignment: FieldAlignment) => void;
}

export function SlidePropertiesPanel({
  field,
  allFields,
  onUpdate,
  onUpdateConfig,
  onClose,
  scoringConfig,
  onUpdateOptionScore,
  onUpdateRules,
  formSettings,
  onUpdateSettings,
  userId,
  formId,
  selectedPage,
  onUpdatePage,
  pages,
  onSetAlignmentAll,
}: SlidePropertiesPanelProps) {
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [bgUploading, setBgUploading] = useState(false);
  const [bgError, setBgError] = useState<string | null>(null);
  const bgInputRef = useRef<HTMLInputElement>(null);
  const [rulesModalOpen, setRulesModalOpen] = useState(false);
  // mobile-ui.6.F F4 — alinhamento pendente aguardando escopo (Só esta/Todas)
  const [alignScopePrompt, setAlignScopePrompt] = useState<FieldAlignment | null>(null);

  // Trocar de campo com o prompt aberto descarta o pendente (evita aplicar
  // no campo errado — os callbacks fecham sobre o field da render atual).
  useEffect(() => {
    setAlignScopePrompt(null);
  }, [field?.id]);

  const { ensureFeature } = useUpgradeGate();

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !userId || !formId || !onUpdateSettings) return;
    e.target.value = '';

    setLogoError(null);

    if (
      !ensureFeature(
        'custom_logo',
        'pro',
        'Upload de logo customizado está disponível nos planos Pro e Business.'
      )
    ) {
      return;
    }

    if (file.size > LOGO_MAX_SIZE) {
      setLogoError('Logo deve ter no máximo 500KB');
      return;
    }

    setLogoUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const filePath = `${userId}/${formId}/logo.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('form-logos')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true,
        });

      if (uploadError) {
        setLogoError(uploadError.message);
        return;
      }

      const publicUrl = getPublicStorageUrl('form-logos', filePath);
      onUpdateSettings({ logoUrl: `${publicUrl}?t=${Date.now()}` });
    } catch {
      setLogoError('Erro ao fazer upload do logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = () => {
    onUpdateSettings?.({ logoUrl: undefined });
  };

  const handleBgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !field || !userId || !formId) return;
    e.target.value = '';
    setBgError(null);

    if (file.size > BG_MAX_SIZE) {
      setBgError('Imagem deve ter no máximo 5MB');
      return;
    }

    setBgUploading(true);
    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
      const filePath = `${userId}/${formId}/bg-${field.id}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('form-logos')
        .upload(filePath, file, {
          contentType: file.type,
          upsert: true,
        });

      if (uploadError) {
        setBgError(uploadError.message);
        return;
      }

      const publicUrl = getPublicStorageUrl('form-logos', filePath);
      onUpdateConfig(field.id, { backgroundImage: `${publicUrl}?t=${Date.now()}` });
    } catch {
      setBgError('Erro ao fazer upload da imagem');
    } finally {
      setBgUploading(false);
    }
  };

  const handleBgRemove = () => {
    if (field) onUpdateConfig(field.id, { backgroundImage: undefined });
  };

  // ── Page properties panel (AC9) ─────────────────────
  if (!field && selectedPage && onUpdatePage) {
    return (
      <div className="w-72 flex-shrink-0 max-h-full liquid-glass rounded-xl p-3 overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-white">Editar página</h3>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white text-lg leading-none"
            title="Fechar"
          >
            ×
          </button>
        </div>
        <div className="space-y-3">
          <Input
            label="Título da página"
            value={selectedPage.title || ''}
            onChange={(e) => onUpdatePage(selectedPage.id, { title: e.target.value })}
          />
          <div className="space-y-2">
            <label className="text-xs text-neutral-400">Layout da Página</label>
            <div className="flex gap-1">
              {([1, 2, 3] as const).map(cols => (
                <button
                  key={cols}
                  onClick={() => onUpdatePage(selectedPage.id, { columns: cols })}
                  className={`flex-1 px-3 py-2 rounded-lg text-xs transition-colors flex flex-col items-center gap-1.5 ${
                    selectedPage.columns === cols
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
                  }`}
                >
                  {/* Grid preview miniature */}
                  <div className="flex gap-0.5 w-full">
                    {Array.from({ length: cols }).map((_, i) => (
                      <div
                        key={i}
                        className={`h-4 rounded-sm flex-1 ${
                          selectedPage.columns === cols
                            ? 'bg-primary-500/30'
                            : 'bg-neutral-600/30'
                        }`}
                      />
                    ))}
                  </div>
                  <span>{cols} col{cols > 1 ? 's' : ''}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!field) return null;

  return (
    <div className="w-72 flex-shrink-0 max-h-full liquid-glass rounded-xl p-3 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white">Editar campo</h3>
        <button
          onClick={onClose}
          className="text-neutral-400 hover:text-white text-lg leading-none"
          title="Fechar"
        >
          ×
        </button>
      </div>

      <div className="space-y-3">
        {/* Label */}
        <FieldLabelInput
          value={field.label}
          onChange={(label) => onUpdate(field.id, { label })}
        />

        {/* Placeholder */}
        <FieldPlaceholderInput
          fieldType={field.type}
          value={field.placeholder || ''}
          onChange={(placeholder) => onUpdate(field.id, { placeholder })}
        />

        {/* Help Text */}
        <FieldHelpTextInput
          value={field.helpText || ''}
          onChange={(helpText) => onUpdate(field.id, { helpText })}
        />

        {/* Required */}
        <FieldRequiredToggle
          fieldType={field.type}
          checked={field.required}
          onChange={(required) => onUpdate(field.id, { required })}
        />

        {/* Options (select, checkbox, radio, multiselect) */}
        <FieldOptionsEditor
          fieldType={field.type}
          options={field.options || []}
          onChange={(options) => onUpdate(field.id, { options })}
        />

        {/* Opinion Scale Config */}
        {field.type === 'opinion_scale' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <p className="text-xs text-neutral-500 uppercase">Configuração da escala</p>
            <div className="grid grid-cols-2 gap-2">
              <Input
                label="Mínimo"
                type="number"
                value={String((field.config as any)?.minValue ?? 1)}
                onChange={(e) => onUpdateConfig(field.id, { minValue: Number(e.target.value) })}
              />
              <Input
                label="Máximo"
                type="number"
                value={String((field.config as any)?.maxValue ?? 10)}
                onChange={(e) => onUpdateConfig(field.id, { maxValue: Number(e.target.value) })}
              />
            </div>
            <Input
              label="Label mínimo"
              value={(field.config as any)?.minLabel ?? ''}
              onChange={(e) => onUpdateConfig(field.id, { minLabel: e.target.value })}
              placeholder="Ex: Nada provável"
            />
            <Input
              label="Label máximo"
              value={(field.config as any)?.maxLabel ?? ''}
              onChange={(e) => onUpdateConfig(field.id, { maxLabel: e.target.value })}
              placeholder="Ex: Muito provável"
            />
          </div>
        )}

        {/* Rating Config */}
        {field.type === 'rating' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <p className="text-xs text-neutral-500 uppercase">Configuração da avaliação</p>
            <Input
              label="Máximo de estrelas"
              type="number"
              value={String((field.config as any)?.maxRating ?? 5)}
              onChange={(e) => onUpdateConfig(field.id, { maxRating: Number(e.target.value) })}
            />
            <Select
              label="Tipo de ícone"
              value={(field.config as any)?.iconType ?? 'star'}
              onChange={(e) => onUpdateConfig(field.id, { iconType: e.target.value })}
              options={[
                { value: 'star', label: 'Estrela' },
                { value: 'heart', label: 'Coração' },
                { value: 'emoji', label: 'Emoji' },
              ]}
            />
          </div>
        )}

        {/* Picture Choice Config */}
        {field.type === 'picture_choice' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <p className="text-xs text-neutral-500 uppercase">Configuração de imagens</p>
            <Checkbox
              label="Permitir múltipla seleção"
              checked={(field.config as any)?.multiSelect ?? false}
              onChange={(e) => onUpdateConfig(field.id, { multiSelect: e.target.checked })}
            />
            <div className="space-y-2">
              {((field.config as any)?.images || []).map((img: any, idx: number) => (
                <div key={idx} className="flex gap-2 items-center">
                  <Input
                    value={img.url || ''}
                    onChange={(e) => {
                      const images = [...((field.config as any)?.images || [])];
                      images[idx] = { ...images[idx], url: e.target.value };
                      onUpdateConfig(field.id, { images });
                    }}
                    placeholder="URL da imagem"
                  />
                  <Input
                    value={img.label || ''}
                    onChange={(e) => {
                      const images = [...((field.config as any)?.images || [])];
                      images[idx] = { ...images[idx], label: e.target.value };
                      onUpdateConfig(field.id, { images });
                    }}
                    placeholder="Label"
                    className="w-24"
                  />
                  <button
                    onClick={() => {
                      const images = ((field.config as any)?.images || []).filter((_: any, i: number) => i !== idx);
                      onUpdateConfig(field.id, { images });
                    }}
                    className="p-1.5 text-neutral-400 hover:text-red-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              <button
                onClick={() => {
                  const images = [...((field.config as any)?.images || []), { url: '', label: '' }];
                  onUpdateConfig(field.id, { images });
                }}
                className="w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors"
              >
                + Adicionar imagem
              </button>
            </div>
          </div>
        )}

        {/* Welcome Screen Config */}
        {field.type === 'welcome_screen' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <Input
              label="Texto do botão"
              value={(field.config as any)?.buttonText ?? 'Começar'}
              onChange={(e) => onUpdateConfig(field.id, { buttonText: e.target.value })}
            />
            <Checkbox
              label="Mostrar descrição"
              checked={(field.config as any)?.showDescription ?? false}
              onChange={(e) => onUpdateConfig(field.id, { showDescription: e.target.checked })}
            />
          </div>
        )}

        {/* Thank You Screen Config */}
        {field.type === 'thank_you_screen' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <Checkbox
              label="Mostrar botão de compartilhar"
              checked={(field.config as any)?.showShareButton ?? false}
              onChange={(e) => onUpdateConfig(field.id, { showShareButton: e.target.checked })}
            />
            <Input
              label="Delay de redirect (s)"
              type="number"
              value={String((field.config as any)?.redirectDelay ?? 0)}
              onChange={(e) => onUpdateConfig(field.id, { redirectDelay: Number(e.target.value) })}
            />
          </div>
        )}

        {/* Redirect URL Config */}
        {field.type === 'redirect_url' && (
          <div className="space-y-3 pt-3 border-t border-neutral-700/50">
            <p className="text-xs text-neutral-500 uppercase">Configuração de redirecionamento</p>
            <div>
              <Input
                label="URL de destino"
                value={(field.config as any)?.url ?? ''}
                onChange={(e) => onUpdateConfig(field.id, { url: e.target.value })}
                placeholder="https://exemplo.com"
              />
              {(field.config as any)?.url && !/^https?:\/\//.test((field.config as any)?.url) && (
                <p className="text-xs text-red-400 mt-1">URL deve começar com http:// ou https://</p>
              )}
            </div>
            <Input
              label="Delay antes do redirect (s)"
              type="number"
              value={String((field.config as any)?.delay ?? 0)}
              onChange={(e) => {
                const v = Math.max(0, Math.min(10, Number(e.target.value)));
                onUpdateConfig(field.id, { delay: v });
              }}
            />
            <Checkbox
              label="Abrir em nova aba"
              checked={(field.config as any)?.openInNewTab ?? false}
              onChange={(e) => onUpdateConfig(field.id, { openInNewTab: e.target.checked })}
            />
            <Checkbox
              label="Ocultar contagem de redirecionamento"
              checked={(field.config as any)?.hideCountdown ?? false}
              onChange={(e) => onUpdateConfig(field.id, { hideCountdown: e.target.checked })}
            />
          </div>
        )}

        {/* Layout Options */}
        {LAYOUT_OPTION_TYPES.includes(field.type) && (
          <div className="space-y-2">
            <label className="text-xs text-neutral-400">Layout das opções</label>
            <div className="grid grid-cols-2 gap-1">
              {(['vertical', 'horizontal', 'grid-2', 'grid-3'] as const).map(layout => (
                <button
                  key={layout}
                  onClick={() => onUpdateConfig(field.id, { optionLayout: layout })}
                  className={`px-2 py-1.5 rounded-lg text-xs transition-colors ${
                    (field.config?.optionLayout || (field.type === 'picture_choice' ? 'grid-2' : 'vertical')) === layout
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
                  }`}
                >
                  {layout === 'vertical' ? 'Vertical' :
                   layout === 'horizontal' ? 'Horizontal' :
                   layout === 'grid-2' ? 'Grid 2col' : 'Grid 3col'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Field Width (Story 5.3 — context-aware by page columns) */}
        {(() => {
          const parentPage = field.pageId ? pages?.find(p => p.id === field.pageId) : undefined;
          const cols = parentPage?.columns ?? 1;
          const widthOptions: { value: 'full' | 'half' | 'third'; label: string; span: string }[] = [
            { value: 'full', label: 'Inteira', span: cols === 1 ? '1/1' : cols === 2 ? '2/2' : '3/3' },
          ];
          if (cols >= 2) widthOptions.push({ value: 'half', label: 'Metade', span: cols === 2 ? '1/2' : '2/3' });
          if (cols >= 3) widthOptions.push({ value: 'third', label: 'Terço', span: '1/3' });

          return (
            <div className="space-y-2">
              <label className="text-xs text-neutral-400">
                Largura do campo{parentPage ? ` (${cols} col${cols > 1 ? 's' : ''})` : ''}
              </label>
              {cols === 1 && parentPage ? (
                <p className="text-xs text-neutral-500">Sempre inteira em páginas de 1 coluna</p>
              ) : (
                <div className="flex gap-1">
                  {widthOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => onUpdate(field.id, { width: opt.value })}
                      className={`flex-1 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                        (field.width || 'full') === opt.value
                          ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                          : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
                      }`}
                    >
                      {opt.label}
                      <span className="ml-1 opacity-60">{opt.span}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })()}

        {/* NEW: Alignment */}
        <div className="space-y-2 pt-3 border-t border-neutral-700/50">
          <label className="block text-sm font-medium text-neutral-200 mb-1.5 text-center">Alinhamento</label>
          <div className="flex gap-1 justify-center">
            {(['left', 'center', 'right'] as const).map(align => (
              <button
                key={align}
                aria-label={align === 'left' ? 'Alinhar à esquerda' : align === 'center' ? 'Centralizar' : 'Alinhar à direita'}
                onClick={() => {
                  // mobile-ui.6.F F4 — pergunta o escopo só quando faz sentido:
                  // handler disponível, campo é "pergunta" (não special screen)
                  // e existe pelo menos outra pergunta pra receber o bulk.
                  const canBulk =
                    !!onSetAlignmentAll &&
                    !PAGE_EXCLUDED_FIELD_TYPES.includes(field.type) &&
                    allFields.some(f => f.id !== field.id && !PAGE_EXCLUDED_FIELD_TYPES.includes(f.type));
                  if (canBulk) setAlignScopePrompt(align);
                  else onUpdate(field.id, { alignment: align });
                }}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  getEffectiveAlignment(field.type, field.alignment) === align
                    ? 'bg-white/10 text-white'
                    : 'text-neutral-400 hover:text-white'
                }`}
              >
                {align === 'left' ? <AlignLeft size={16} /> : align === 'center' ? <AlignCenter size={16} /> : <AlignRight size={16} />}
              </button>
            ))}
          </div>
          {alignScopePrompt && (
            <AlignmentScopePrompt
              onOnlyThis={() => {
                onUpdate(field.id, { alignment: alignScopePrompt });
                setAlignScopePrompt(null);
              }}
              onAll={() => {
                onSetAlignmentAll?.(alignScopePrompt);
                setAlignScopePrompt(null);
              }}
            />
          )}
        </div>

        {/* Background Image Upload */}
        <div>
          <label className="block text-xs text-neutral-400 mb-1.5 text-center">Imagem de fundo</label>
          {(field.config as any)?.backgroundImage ? (
            <div className="relative rounded-lg overflow-hidden border border-neutral-700/50">
              <img
                src={(field.config as any).backgroundImage}
                alt="Background"
                className="w-full h-20 object-cover"
              />
              <button
                onClick={handleBgRemove}
                className="absolute top-1 right-1 p-1 bg-neutral-900/80 rounded-md hover:bg-red-500/80 transition-colors"
                title="Remover imagem"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => bgInputRef.current?.click()}
              disabled={bgUploading}
              className="w-full flex items-center justify-center gap-2 py-3 border border-dashed border-neutral-700 rounded-lg text-xs text-neutral-400 hover:text-white hover:border-neutral-500 transition-colors disabled:opacity-50"
            >
              {bgUploading ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando...</>
              ) : (
                <><Upload className="w-3.5 h-3.5" /> Upload imagem (max 5MB)</>
              )}
            </button>
          )}
          <input
            ref={bgInputRef}
            type="file"
            accept={BG_ACCEPTED}
            onChange={handleBgUpload}
            className="hidden"
          />
          {bgError && <p className="text-xs text-red-400 mt-1">{bgError}</p>}

          {/* Blur + Opacity sliders — visíveis apenas quando há backgroundImage (Story saas-1.6) */}
          {(field.config as any)?.backgroundImage && (
            <div className="mt-3 space-y-3">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-neutral-400">Desfoque</label>
                  <span className="text-xs text-neutral-500">
                    {(field.config as any)?.backgroundBlur ?? 0}px
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={(field.config as any)?.backgroundBlur ?? 0}
                  onChange={(e) => onUpdateConfig(field.id, { backgroundBlur: Number(e.target.value) })}
                  className="w-full accent-primary-500"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs text-neutral-400">Opacidade</label>
                  <span className="text-xs text-neutral-500">
                    {(field.config as any)?.backgroundOpacity ?? 100}%
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={(field.config as any)?.backgroundOpacity ?? 100}
                  onChange={(e) => onUpdateConfig(field.id, { backgroundOpacity: Number(e.target.value) })}
                  className="w-full accent-primary-500"
                />
              </div>
            </div>
          )}
        </div>

        {/* NEW: "Outros" toggle (radio/checkbox only) */}
        {['radio', 'checkbox'].includes(field.type) && (
          <Checkbox
            label='Incluir opção "Outros"'
            checked={(field.config as any)?.allowOther || false}
            onChange={(e) => onUpdateConfig(field.id, { allowOther: e.target.checked })}
          />
        )}

        {/* Logo / Marca */}
        {onUpdateSettings && (
          <div className="space-y-2 pt-3 border-t border-neutral-700/50">
            <label className="block text-sm font-medium text-neutral-200 mb-1.5 text-center">Logo / Marca</label>

            {formSettings?.logoUrl ? (
              <div className="relative group rounded-lg overflow-hidden bg-neutral-800/50 p-3 flex items-center gap-3">
                <img
                  src={formSettings.logoUrl}
                  alt="Logo"
                  className="h-10 w-auto max-w-[120px] object-contain"
                />
                <button
                  type="button"
                  onClick={handleLogoRemove}
                  className="ml-auto p-1 rounded-md text-neutral-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title="Remover logo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => logoInputRef.current?.click()}
                disabled={logoUploading}
                className="w-full p-3 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors flex items-center justify-center gap-2"
              >
                {logoUploading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                ) : (
                  <><Upload className="w-4 h-4" /> Upload do logo</>
                )}
              </button>
            )}

            <input
              ref={logoInputRef}
              type="file"
              accept={LOGO_ACCEPTED}
              onChange={handleLogoUpload}
              className="hidden"
            />

            {logoError && <p className="text-xs text-red-400">{logoError}</p>}

            {formSettings?.logoUrl && (
              <>
                <label className="block text-xs text-neutral-400 text-center">Posição do logo</label>
                <div className="grid grid-cols-3 gap-0.5 w-28 mx-auto">
                  {([
                    { value: 'top-left',      label: '↖', title: 'Superior esquerda' },
                    { value: 'top-center',    label: '↑', title: 'Superior centro' },
                    { value: 'top-right',     label: '↗', title: 'Superior direita' },
                    null, // middle-left (slot vazio)
                    { value: 'middle-center', label: '•', title: 'Meio / centro (acima da pergunta)' },
                    null, // middle-right (slot vazio)
                    { value: 'bottom-left',   label: '↙', title: 'Inferior esquerda' },
                    { value: 'bottom-center', label: '↓', title: 'Inferior centro' },
                    { value: 'bottom-right',  label: '↘', title: 'Inferior direita' },
                  ] as ({ value: LogoPosition; label: string; title: string } | null)[]).map((pos, idx) => {
                    if (!pos) return <div key={`empty-${idx}`} className="aspect-square" />;
                    const isActive = (formSettings?.logoPosition || 'top-left') === pos.value;
                    return (
                      <button
                        key={pos.value}
                        onClick={() => onUpdateSettings({ logoPosition: pos.value })}
                        title={pos.title}
                        className={`aspect-square flex items-center justify-center rounded-md text-xs leading-none transition-colors ${
                          isActive
                            ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                            : 'bg-neutral-800/50 text-neutral-400 hover:text-white'
                        }`}
                      >
                        {pos.label}
                      </button>
                    );
                  })}
                </div>

                {/* Slider de tamanho do logo (50–200%, default 100) — Story saas-1.7 */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-neutral-400">Tamanho</label>
                    <span className="text-xs text-neutral-500">
                      {formSettings?.logoScale ?? 100}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min={50}
                    max={200}
                    step={5}
                    value={formSettings?.logoScale ?? 100}
                    onChange={(e) => onUpdateSettings({ logoScale: Number(e.target.value) })}
                    className="w-full accent-primary-500"
                  />
                </div>

                {/* Slider de margem do logo (0–64px, default 16) — Story saas-1.6 */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs text-neutral-400">Margem</label>
                    <span className="text-xs text-neutral-500">
                      {formSettings?.logoMargin ?? 16}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={64}
                    step={1}
                    value={formSettings?.logoMargin ?? 16}
                    onChange={(e) => onUpdateSettings({ logoMargin: Number(e.target.value) })}
                    className="w-full accent-primary-500"
                  />
                </div>

                {/* Change logo button when one already exists */}
                <button
                  type="button"
                  onClick={() => logoInputRef.current?.click()}
                  disabled={logoUploading}
                  className="w-full p-2 text-xs text-neutral-400 hover:text-white transition-colors"
                >
                  {logoUploading ? 'Enviando...' : 'Trocar logo'}
                </button>
              </>
            )}
          </div>
        )}

        {/* Rules Button + Modal */}
        {!NON_INPUT_FIELD_TYPES.includes(field.type) && onUpdateRules && (
          <>
            <button
              type="button"
              onClick={() => setRulesModalOpen(true)}
              className="w-full p-2 border border-dashed border-neutral-700 rounded-xl text-sm text-neutral-400 hover:text-white hover:border-neutral-600 transition-colors flex items-center justify-center gap-1.5"
            >
              <GitBranch className="w-3.5 h-3.5" />
              Regras
              {(field.rules?.length || 0) > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-primary-500/20 text-primary-400 text-[10px] font-medium">
                  {field.rules!.length}
                </span>
              )}
            </button>
            <RuleEditorModal
              isOpen={rulesModalOpen}
              onClose={() => setRulesModalOpen(false)}
              field={field}
              allFields={allFields}
              scoringVariables={(scoringConfig?.variables as ScoreVariable[]) || []}
              onUpdateRules={onUpdateRules}
              pages={pages}
            />
          </>
        )}

        {/* Score Points Per Option */}
        {scoringConfig?.enabled &&
          (scoringConfig?.variables?.length ?? 0) > 0 &&
          SCORABLE_TYPES.includes(field.type) && (
          <div className="mt-3 pt-3 border-t border-neutral-700/50">
            <p className="text-xs text-neutral-500 uppercase mb-2">Pontuação por opção</p>
            {(field.options || []).map((rawOpt, optIndex) => {
              const normalizedOpt = typeof rawOpt === 'string'
                ? { label: rawOpt, value: rawOpt, scores: [] as FieldOptionScore[] }
                : rawOpt;

              return (
                <div key={optIndex} className="mb-3 p-2 rounded-lg bg-neutral-900/30">
                  <p className="text-xs text-neutral-300 mb-1.5">{normalizedOpt.label}</p>
                  <div className="space-y-1">
                    {(scoringConfig?.variables || []).map(variable => {
                      const score = normalizedOpt.scores?.find(s => s.variableId === variable.id);
                      return (
                        <div key={variable.id} className="flex items-center gap-2">
                          <span className="text-xs text-neutral-500 w-20 truncate">{variable.name}</span>
                          <Input
                            type="number"
                            value={String(score?.points ?? 0)}
                            onChange={(e) => onUpdateOptionScore?.(
                              field.id, optIndex, variable.id, Number(e.target.value)
                            )}
                            className="w-16 text-xs"
                          />
                          <span className="text-xs text-neutral-500">pts</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
